import { ChangeDetectionStrategy, Component, computed, inject, OnInit, output, signal } from '@angular/core';

import { GridOptions, GridEmitter, UiGridComponent } from '@program-suite/ui-grid';
import { ViewSubmissionStore, PolicyChangeGridViewModel, PolicyEndorsementsService } from '@program-suite/data-common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AdditionalInsuredFormComponent,
  CoverageAdjustmentFormComponent,
  PolicyEndorsementFormComponent,
  PolicyModalTypeEnum,
  getModalTypeFromChangeType,
  VoidEndorsementComponent,
} from '@program-suite/feature-policy-endorsements';
import { ModalService, ModalType } from '@program-suite/ui';
import { AuthStore } from '@program-suite/data-auth';

@Component({
  selector: 'lib-endorsements',
  templateUrl: './endorsements.component.html',
  styleUrls: ['./endorsements.component.scss', '../view-submission.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UiGridComponent],
})
export class EndorsementsComponent implements OnInit {
  authStore = inject(AuthStore);
  viewSubmissionStore = inject(ViewSubmissionStore);
  modalService = inject(ModalService);
  policyChangeService = inject(PolicyEndorsementsService);
  changeTabEvent = output<string>();
  id: string | null = null;
  pageSizeMax = 2147483647;
  totalNumberOfItems = computed<number>(() => this.viewSubmissionStore.endorsements?.()?.rows?.length ?? 0);
  // TODO: Add type for Endorsements
  data = computed<PolicyChangeGridViewModel[]>(
    () => (this.viewSubmissionStore.endorsements?.()?.rows as PolicyChangeGridViewModel[]) ?? [],
  );
  filterValues = signal<any>({});
  gridOptions = signal<GridOptions>({
    filterData: [],
    filterValues: this.filterValues,
    openFilters: true,
    columns: [
      { header: 'Created', controlName: 'createDate', displayPipe: 'date' },
      { header: 'Type', controlName: 'changeType', displayPipe: 'space' },
      { header: 'Description', controlName: 'policyChangeType', displayPipe: 'space' },
      { header: 'Reason', controlName: 'details', displayPipe: 'truncate', maxWidth: '450px' },
      { header: 'Number', controlName: 'endorsementNumber' },
      { header: 'Status', controlName: 'policyChangeStatus', displayPipe: 'space' },
      { header: 'Modified By', controlName: 'lastUpdatedBy' },
      {
        header: 'Manage',
        controlName: 'action',
        actionColumnitems: [
          {
            type: 'dropdown',
            buttonAction: 'action',
            conditionalDisplay: { calculateDisplay: () => 'ellipsis', column: 'Manage' },
            dropdownItems: computed(() => {
              if (this.authStore.isAdmin()) {
                let items = [
                  {
                    buttonAction: 'action',
                    conditionalName: {
                      column: 'policyChangeStatus',
                      calculateDisplay: (status: string) => {
                        switch (status) {
                          case 'Draft':
                            return 'Edit';
                          case 'Requested':
                            return 'Edit';
                          default:
                            return 'View';
                        }
                      },
                    },
                  },
                  {
                    buttonAction: 'download',
                    name: 'Download',
                    conditionalDisabled: {
                      calculateDisabled: (bool: string) => {
                        let arr = ['Requested', 'Declined', 'Draft', 'PendingSignature'];
                        if (arr.includes(bool)) {
                          return true;
                        }
                        return false;
                      },
                      column: 'policyChangeStatus',
                    },
                  },

                  {
                    buttonAction: 'void',
                    name: 'Void',
                    conditionalDisabled: { calculateDisabled: (bool: boolean) => bool !== true, column: 'isVoidable' },
                  },
                ];
                return items;
              } else {
                let items = [
                  {
                    buttonAction: 'action',
                    conditionalName: {
                      column: 'policyChangeStatus',
                      calculateDisplay: (status: string) => {
                        switch (status) {
                          case 'Draft':
                            return 'Edit';
                          default:
                            return 'View';
                        }
                      },
                    },
                  },

                  {
                    buttonAction: 'download',
                    name: 'Download',
                    conditionalDisabled: {
                      calculateDisabled: (bool: string) => {
                        let arr = ['Requested', 'Declined', 'Draft', 'PendingSignature'];
                        if (arr.includes(bool)) {
                          return true;
                        }
                        return false;
                      },
                      column: 'policyChangeStatus',
                    },
                  },
                ];

                return items;
              }
            }),
          },
        ],
      },
    ],
    data: this.data,
    frontEndPagination: true,
    fetchData: false,
    displaySearch: false,
    hidepagination: true,
  });

  constructor(private route: ActivatedRoute, private router: Router) {}

  ngOnInit() {
    this.id = this.route.snapshot.paramMap.get('id');
  }

  fetchAll(rawParams?: any) {
    const params = {
      policyGuid: this.id,
      pageSize: this.pageSizeMax,
      pageNumber: 1,
      dateFrom: '',
      dateTo: '',
    };
    this.viewSubmissionStore.getEndorsements(params);
  }

  actionEvent(event: GridEmitter) {
    if (event.action === 'fetchAll') {
      this.fetchAll(event.data);
    } else if (event.action === 'formChanges') {
      // this.fetchAll(event.data);
    } else if (event.action === 'clearFilters') {
      // this.fetchAll(event.data);
    } else if (event.action === 'onDestroy') {
      // this.submissionGridStore.setFilterValues(event.data ?? {});
    } else if (event.action === 'download') {
      //@ts-ignore
      this.policyChangeService.downloadPolicyChange(event?.data?.policyChangeGuid);
    } else if (event.action === 'action') {
      //@ts-ignore
      if (getModalTypeFromChangeType(event?.data?.changeType) === PolicyModalTypeEnum.Choice) {
        this.openEndorsementModal(event.data);
      } //@ts-ignore
      else if (getModalTypeFromChangeType(event?.data?.changeType) === PolicyModalTypeEnum.CoverageAdjustment) {
        this.gotoCoverageAdjustmentForm(event.data);
      } else {
        this.gotoAdditionalInsuredForm(event.data);
      }
    } else if (event.action === 'void') {
      this.openVoidEndorsementModal(event.data);
    }
  }

  openEndorsementModal(gridData?: any) {
    const data = {
      ...gridData,
      policyGuid: this.id,
      policyNumber: this.viewSubmissionStore.summary?.()?.policyNumber,
      insuredName: this.viewSubmissionStore.summary?.()?.businessInfo?.businessName,
    };
    this.modalService.open(
      PolicyEndorsementFormComponent,
      {
        modalType: ModalType.Medium,
        data: data,
      },
      (result) => {},
    );
  }

  gotoAdditionalInsuredForm(item: any) {
    const data = {
      ...item,
      policyGuid: this.id,
      policyNumber: this.viewSubmissionStore.summary?.()?.policyNumber,
      businessName: this.viewSubmissionStore.summary?.()?.businessInfo?.businessName,
    };
    this.router.navigate(['/policies', 'endorsements', this.id, 'additional-insured', item.policyChangeGuid], {
      state: {
        ...data,
      },
    });
  }

  gotoCoverageAdjustmentForm(item: any) {
    const data = {
      ...item,
      policyGuid: this.id,
      policyNumber: this.viewSubmissionStore.summary?.()?.policyNumber,
      businessName: this.viewSubmissionStore.summary?.()?.businessInfo?.businessName,
      subTitle: `${this.viewSubmissionStore.summary?.()?.businessInfo?.businessName} | ${
        this.viewSubmissionStore.summary?.()?.policyNumber
      }`,
      mode: 'edit',
    };

    this.router.navigate(['/policies', 'endorsements', this.id, 'coverage-adjustment', item.policyChangeGuid], {
      state: {
        ...data,
      },
    });
  }

  openVoidEndorsementModal(item: any) {
    this.modalService.open(
      VoidEndorsementComponent,
      {
        modalType: ModalType.Medium,
        data: {
          ...item,
          policyGuid: this.id,
          policyNumber: this.viewSubmissionStore.summary?.()?.policyNumber,
          businessName: this.viewSubmissionStore.summary?.()?.businessInfo?.businessName,
          subTitle: `${this.viewSubmissionStore.summary?.()?.businessInfo?.businessName} | ${
            this.viewSubmissionStore.summary?.()?.policyNumber
          }`,
        },
      },
      (success) => {
        if (success) {
          this.fetchAll();
        }
      },
    );
  }
}
